import { usePreferences } from 'Hooks/preferences/usePreferences';
import { MobXProviderContext } from 'mobx-react';
import { isTerminalState } from 'Modules/softphone/constants';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import { softphonePanelLogger as logger } from 'Modules/softphone/utils/logger';
import { useContext } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';

interface UseSoftphonePanelParams {
  isNewSoftphoneEnabled?: boolean;
}

/**
 * Check if a call is in an active state (not terminal)
 * Terminal states (idle, ended, failed, busy, no-answer) should close the UI immediately
 * to prevent a visual flash of idle content during the cleanup delay.
 */
const isCallActive = (call: CallSession): boolean => {
  return !isTerminalState(call.state);
};

/**
 * Hook to manage softphone panel visibility and state
 *
 * Determines whether the dialpad and call panels should be open based on:
 * - Softphone type (new Zustand-based vs legacy MobX-based)
 * - User preferences (floating vs fixed softphone)
 * - Active call states
 */
const useSoftphonePanel = ({
  isNewSoftphoneEnabled,
}: UseSoftphonePanelParams = {}) => {
  const {
    conversation: conversationStore,
    phoneCall: phoneStore,
  } = useContext<RootStoreProps>(MobXProviderContext);

  const {
    preferences: { floatingSoftphone: isSoftphoneFixed },
  } = usePreferences();

  // New softphone store (Zustand)
  const { activeCalls, isDialpadActive } = useSoftphoneStore();

  // Check if there's an active call based on which softphone is enabled
  // For new softphone: Only show when there are ACTIVE calls (answered), not incoming or idle
  // Incoming calls show only the banner notification until answered
  // Idle calls should never show the softphone UI
  // For legacy softphone: Keep existing behavior with all connection states
  const isActiveCall = isNewSoftphoneEnabled
    ? Array.from(activeCalls.values()).some(isCallActive)
    : phoneStore.AnyPhoneConnectionActive ||
      phoneStore.isCallTimeout ||
      conversationStore.listOfIncomingVideoCalls.size > 0 ||
      phoneStore.isAttemptingToReconnectSip ||
      phoneStore.isWebrtcAttemptDone;

  const activeCallsList = Array.from(activeCalls.values());
  const nonIdleCallsList = activeCallsList.filter(isCallActive);

  logger.info('State:', {
    isNewSoftphoneEnabled,
    isSoftphoneFixed,
    totalCalls: activeCalls.size,
    nonIdleCalls: nonIdleCallsList.length,
    isActiveCall,
    isCallOpen: isSoftphoneFixed && isActiveCall,
  });

  return {
    isDialpadOpen: isSoftphoneFixed && isDialpadActive,
    isCallOpen: isSoftphoneFixed && isActiveCall,
  };
};

export default useSoftphonePanel;
